import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextArea,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import type { MailAlias } from '../../../types/models/MailAlias';
import type { ApiResponseMailAlias } from '../../../types/api/ApiResponseMailAlias';

interface MailAliasEditModalProps {
  alias: MailAlias | null;
  onClose: () => void;
  mailDomainId: number;
  domainName: string;
  onSuccess: () => void;
}

const SOURCE_REGEX = /^(@|[A-Za-z0-9._%+-]+)$/;
const SOURCE_MAX_LENGTH = 40;
const DESTINATION_MAX_LENGTH = 2048;

function MailAliasEditModal({ alias, onClose, mailDomainId, domainName, onSuccess }: MailAliasEditModalProps) {
  const apiFetch = useApiFetch();
  const [source, setSource] = useState('');
  const [destination, setDestination] = useState('');
  const [originalSource, setOriginalSource] = useState('');
  const [originalDestination, setOriginalDestination] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (alias) {
      // A catch-all is stored as the literal '@'; the field shows it as an empty local part.
      setSource(alias.source === '@' ? '' : alias.source);
      setDestination(alias.destination);
      setOriginalSource(alias.source);
      setOriginalDestination(alias.destination);
      setError(null);
      setWarning(null);
      setFieldErrors({});
    }
  }, [alias]);

  const trimmedSource = source.trim();
  // An empty source (or a typed '@') is the domain-wide catch-all, which the backend stores as '@'.
  const canonicalSource = trimmedSource === '' ? '@' : trimmedSource;
  const isCatchAll = canonicalSource === '@';
  const trimmedDestination = destination.trim();
  const isSourceValid = canonicalSource.length <= SOURCE_MAX_LENGTH && SOURCE_REGEX.test(canonicalSource);
  const isDestinationValid = trimmedDestination.length > 0 && trimmedDestination.length <= DESTINATION_MAX_LENGTH;
  const sourceChanged = canonicalSource !== originalSource;
  const destinationChanged = trimmedDestination !== originalDestination;
  const hasChanges = sourceChanged || destinationChanged;
  const isFormValid = isSourceValid && isDestinationValid && hasChanges && warning === null;

  const previewAddress = isCatchAll ? `@${domainName}` : `${canonicalSource}@${domainName}`;

  const resetAndClose = () => {
    setSource('');
    setDestination('');
    setOriginalSource('');
    setOriginalDestination('');
    setError(null);
    setWarning(null);
    setFieldErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    if (!alias) return;
    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      const body: Record<string, string> = {};
      if (sourceChanged) body.source = canonicalSource;
      if (destinationChanged) body.destination = trimmedDestination;

      const params = new URLSearchParams({ mail_domain_id: String(mailDomainId) });
      const response = await apiFetch(`/emails/aliases/${alias.id}?${params.toString()}`, {
        method: 'PATCH',
        headers: { Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      const result = (await response.json().catch(() => null)) as
        | (ApiResponseMailAlias & { data?: Record<string, string> })
        | null;

      if (response.status === 200 && result?.status === 'success') {
        onSuccess();
        resetAndClose();
        return;
      }

      if (response.status === 404) {
        setWarning(
          result?.message ?? 'This alias was deleted by another user. Close this dialog and refresh the list.',
        );
      } else if (response.status === 422 && result?.status === 'fail' && result.data) {
        const errors: Record<string, string> = {};
        for (const [key, val] of Object.entries(result.data)) {
          errors[key] = String(val);
        }
        setFieldErrors(errors);
      } else {
        setError(result?.message ?? `HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update alias');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal variant="small" isOpen={alias !== null} onClose={resetAndClose} aria-label="Edit alias">
      <ModalHeader
        title={`Edit alias — ${originalSource === '@' ? `@${domainName}` : `${originalSource}@${domainName}`}`}
      />
      <ModalBody>
        {warning && (
          <Alert variant="warning" isInline title="Alias no longer exists" className="pf-v6-u-mb-md">
            {warning}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" isInline title="Update failed" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Form>
          <FormGroup label="Source" fieldId="edit-alias-source">
            <TextInput
              id="edit-alias-source"
              value={source}
              onChange={(_event, value) => {
                setSource(value);
                if (fieldErrors.source) {
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.source;
                    return next;
                  });
                }
              }}
              validated={fieldErrors.source || !isSourceValid ? 'error' : 'default'}
            />
            {fieldErrors.source ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{fieldErrors.source}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : !isSourceValid ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">
                    Use letters, numbers, dots, hyphens, underscores, plus signs, or percent signs (max 40), or leave it
                    empty for a domain-wide catch-all.
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>
                    Email address: <strong>{previewAddress}</strong>
                    {isCatchAll && ' (catch-all for the whole domain)'}
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
          <FormGroup label="Destination" isRequired fieldId="edit-alias-destination">
            <TextArea
              id="edit-alias-destination"
              value={destination}
              onChange={(_event, value) => {
                setDestination(value);
                if (fieldErrors.destination) {
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.destination;
                    return next;
                  });
                }
              }}
              rows={3}
              resizeOrientation="vertical"
              aria-label="Destination addresses"
              validated={fieldErrors.destination || (destination && !isDestinationValid) ? 'error' : 'default'}
            />
            {fieldErrors.destination ? (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{fieldErrors.destination}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            ) : (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem>
                    Comma-separated list of email addresses. Use multiple destinations to fan out delivery.
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={handleSubmit}
          isDisabled={!isFormValid || isSubmitting}
          isLoading={isSubmitting}
        >
          Save changes
        </Button>
        <Button variant="link" onClick={resetAndClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default MailAliasEditModal;
